import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { loginWithKakao, loginWithNaver } from './authService';
import { consumeNaverState, kakaoRedirectUri } from './socialAuth';
import { useUserSession } from './userSession';
import styles from './Auth.module.scss';

interface OAuthCallbackProps {
  provider: 'kakao' | 'naver';
}

function OAuthCallback({ provider }: OAuthCallbackProps) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [, setSession] = useUserSession();
  const [error, setError] = useState<string | null>(null);
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const code = searchParams.get('code');
    const returnedState = searchParams.get('state');

    if (searchParams.get('error') || !code) {
      setError('로그인이 취소되었거나 오류가 발생했습니다.');
      return;
    }

    const run = async () => {
      try {
        let result;
        if (provider === 'naver') {
          const expectedState = consumeNaverState();
          if (!expectedState || expectedState !== returnedState) {
            setError('로그인 요청이 유효하지 않습니다. 다시 시도해주세요.');
            return;
          }
          result = await loginWithNaver(code, returnedState);
        } else {
          result = await loginWithKakao(code, kakaoRedirectUri());
        }

        if ('signupRequired' in result) {
          navigate('/signup', { replace: true, state: { socialSignup: result } });
          return;
        }
        setSession(result);
        navigate('/mypage', { replace: true });
      } catch (err) {
        setError(err instanceof Error ? err.message : '로그인 중 오류가 발생했습니다.');
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={`${styles.page} ${styles.pageCenter}`}>
      <section className={styles.formCard}>
        {error ? (
          <>
            <p className={styles.errorBox}>{error}</p>
            <div className={styles.helpLinks}>
              <Link to="/login">로그인으로 돌아가기</Link>
            </div>
          </>
        ) : (
          <p>로그인 처리 중입니다...</p>
        )}
      </section>
    </div>
  );
}

export default OAuthCallback;
